import { isProgressNotePlaceholder } from './progressNoteBody';

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function educationLines(education) {
  if (!Array.isArray(education)) return [];
  return education
    .map((item) => String(item || '').trim())
    .filter((item) => item && !isProgressNotePlaceholder(item));
}

function medicationLines(meds) {
  if (!Array.isArray(meds)) return [];
  return meds
    .map((med) => {
      if (typeof med === 'string') return med.trim();
      return [med?.name || med?.drug_name, med?.dose, med?.route, med?.frequency]
        .filter(Boolean)
        .join(' ')
        .trim();
    })
    .filter(Boolean);
}

function followUpLine(followUp) {
  if (!followUp || typeof followUp !== 'object') return '';
  return [followUp.physician, followUp.timeframe, followUp.location]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' — ');
}

/** Flatten legacy structured discharge fields into one body (for older notes). */
export function flattenStructuredDischargeNote(note) {
  if (!note) return '';
  const lines = [];

  if (hasText(note.condition_at_discharge)) {
    lines.push(`Condition at discharge\n${note.condition_at_discharge.trim()}`);
  }
  if (hasText(note.discharge_mode)) {
    lines.push(`Discharge mode\n${note.discharge_mode.trim()}`);
  }
  if (hasText(note.accompanied_by)) {
    lines.push(`Accompanied by\n${note.accompanied_by.trim()}`);
  }
  if (hasText(note.special_instructions)) {
    lines.push(`Special instructions\n${note.special_instructions.trim()}`);
  }

  const education = educationLines(note.education);
  if (education.length) {
    lines.push(`Discharge education\n${education.join('\n')}`);
  }

  const meds = medicationLines(note.discharge_medications);
  if (meds.length) {
    lines.push(`Discharge medications\n${meds.join('\n')}`);
  }

  const followUp = followUpLine(note.follow_up);
  if (followUp) {
    lines.push(`Follow-up\n${followUp}`);
  }

  const understanding = [
    note.understanding_patient ? 'Patient understands' : null,
    note.understanding_family ? 'Family understands' : null,
  ].filter(Boolean);
  if (understanding.length) {
    lines.push(understanding.join('; '));
  }

  return lines.join('\n\n');
}

/** Free-text body for discharge notes (replaces structured discharge UI). */
export function getDischargeNoteBody(note) {
  if (!note) return '';
  // Return narrative exactly as stored so typing (spaces, newlines) is not rewritten.
  if (typeof note.narrative === 'string' && note.narrative.length > 0) {
    return note.narrative;
  }
  const flattened = flattenStructuredDischargeNote(note);
  if (flattened) return flattened;
  return typeof note.narrative === 'string' ? note.narrative : '';
}

export function setDischargeNoteBody(note, text) {
  const value = typeof text === 'string' ? text : '';
  return {
    ...(note || {}),
    note_type: 'discharge',
    narrative: value,
    // Keep legacy fields empty so UI/PDF prefer the single narrative body.
    condition_at_discharge: '',
    discharge_mode: '',
    accompanied_by: '',
    special_instructions: '',
    education: [],
    discharge_medications: Array.isArray(note?.discharge_medications)
      ? note.discharge_medications
      : [],
    follow_up: { physician: '', timeframe: '', location: '' },
    understanding_patient: Boolean(note?.understanding_patient),
    understanding_family: Boolean(note?.understanding_family),
  };
}

export function dischargeNoteHasContent(note) {
  return hasText(getDischargeNoteBody(note));
}
